import React, { useState, useCallback, useEffect } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  AutoComplete,
  Input,
  Checkbox,
  DatePicker,
} from "antd";
import { keys } from "lodash";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import moment from "moment-timezone";
import {
  uploadPesticideReportData,
  getCommodityMrlValues,
  generatePesticideReportHeader,
} from "../../../../../actions/chemistry";
import ChemPesticidesTableRow from "./ChemPesticidesTableRow";
import ChemPesticidesTableCell from "./ChemPesticidesTableCell";
import ChemPesticidePreview from "./ChemPesticidePreview";
import ConfirmationModal from "../../../../Common/ConfirmationModal";
import { getUser } from "../../../../../utils/helpers";
import GeneratePDF from "./GeneratePesticidesPDF";
import "./ChemPesticidesTable.css";
import "./ChemPesticidesModal.css";

function ChemPesticidesModal({
  compoundTableData,
  tableColumns,
  closeModal,
  parentSampleDetail,
  pesticideSampleInfo,
  readyP,
  showSubmit,
  instrumentList,
}) {
  const [dataSource, setDataSource] = useState([...compoundTableData]);
  const [tableForm, setTableForm] = useState([]);
  const [, setNewRowKey] = useState(compoundTableData.length);
  const [baseForm] = Form.useForm();
  const [formLoading, setFormLoading] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [fetchBaseList, setFetchBaseList] = useState(false);
  const [compoundNameToItem, setCompoundNameToItem] = useState(null);
  const [commodityNameToItem, setCommodityNameToItem] = useState(null);
  const [sampleDetail, setSampleDetail] = useState({ ...parentSampleDetail });
  const [latestPesticideData, setLatestPesticideData] = useState(null); // This is used in the PDF preview component
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [compoundCounterMap, setCompoundCounterMap] = useState(new Map());
  /** PDF Preview */
  const [pdfModalData, setPdfModalData] = useState({
    open: false,
    pdfUrl: null,
    pdf: null,
    user_id: getUser().id,
  });

  /** Set up two maps
   * Compound name to compound item
   * Commodity name to commodity item
   */
  useEffect(() => {
    const compoundMap = {};
    parentSampleDetail.all_compounds.forEach((compoundItem) => {
      compoundMap[compoundItem.compound.toLowerCase()] = compoundItem;
    });
    setCompoundNameToItem(compoundMap);
    const commodityMap = {};
    parentSampleDetail.all_commodities.forEach((commodityItem) => {
      commodityMap[commodityItem.commodity.toLowerCase()] = commodityItem;
    });
    setCommodityNameToItem(commodityMap);

    // Set up a map to validator if a compound is duplicated in the table
    const repeatedMap = new Map();
    parentSampleDetail.values.forEach((compoundItem) => {
      const compound = compoundItem.compound.toLowerCase();
      const curNum = repeatedMap.has(compound) ? repeatedMap.get(compound) + 1 : 1;
      repeatedMap.set(compound, curNum);
    });
    setCompoundCounterMap(repeatedMap);
  }, [parentSampleDetail]);

  /**
   * When the compound input is on blur or on select, we also need to update the map to keep track
   * of each compound counter, each compound should only appear once in the table.
   */
  const checkAndUpdateCompoundMap = async (valueToAdd, valueToRemove) => {
    if (valueToAdd) {
      valueToAdd = valueToAdd.toLowerCase();
      const addNum = compoundCounterMap.has(valueToAdd) ? compoundCounterMap.get(valueToAdd) + 1 : 1;
      compoundCounterMap.set(valueToAdd, addNum);
    }

    if (valueToRemove) {
      valueToRemove = valueToRemove.toLowerCase();
      const removeNum = compoundCounterMap.has(valueToRemove) ? compoundCounterMap.get(valueToRemove) - 1 : 0;
      compoundCounterMap.set(valueToRemove, removeNum);
    }
    // Update the duplicated validator status for all compound forms
    if (valueToAdd || valueToRemove) {
      tableForm.forEach((form) => {
        const compoundValue = form.getFieldValue("compound");
        if (compoundValue) {
          form.validateFields(["compound"]);
        }
      });
    }
  };

  /**
   * For each row form, when it is created, we save each form in the tableForm array.
   * This function will be called only when a specific row is updated
   */
  const saveFormInstance = useCallback((form, index) => {
    setTableForm((prevForms) => {
      const newForms = [...prevForms];
      newForms[index] = form;
      return newForms;
    });
  }, []);

  /**
   * If we found the original compound name based on the current input value.
   * If no record found in the original compound list, then we use the current input value
   */
  const getValidCompoundItem = (input) => {
    const { compound: name, code } = compoundNameToItem[input.toLowerCase()] ?? { compound: input, code: -1 };
    return { name, code };
  };

  /**
   * This function will create a list of all compound data for the
   * "values" key in the payload of pesticidesdata/
   */
  const getCompoundValuesList = (rows) => {
    const values = [];
    const skipKeys = ["key", "editable", "rowIndex", "compound", "compound_result"];
    const compoundNoEdit = ["Other Tested Pesticides Below LOQ*"];
    rows.forEach((row) => {
      const skip = !row.compound || compoundNoEdit.includes(row.compound);
      if (!skip) {
        const { compound, compound_result: value } = row;
        const compoundData = { compound: getValidCompoundItem(compound).name, value, data: [] };
        const countries = keys(row).filter((itemKey) => !skipKeys.includes(itemKey));
        countries.forEach((country) => {
          compoundData.data.push({
            country,
            mrl_value: row[country] ?? "",
          });
        });
        values.push(compoundData);
      }
    });
    return values;
  };

  /** Display the pesticide PDF preview */
  const showPreview = (blobURL, pdf, reportData) => {
    const {
      report_id, report_name, received_date, report_date,
    } = reportData;
    const {
      genista_id,
      workorder_id,
      pesticides_attachment,
      test_name,
    } = sampleDetail;

    setPdfModalData({
      user_id: getUser().id,
      open: true,
      pdfUrl: blobURL,
      pdf,
      report_name,
      report_id,
      genista_id,
      workorder_id,
      pesticides_attachment,
      test_name,
      report_date,
      received_date,
    });
  };

  /**
   * Generate a pesticide PDF report
   */
  const generatePdfPreview = async (reportData, form) => {
    try {
      const {
        received_date,
        report_id: rpID,
        to: toID,
      } = reportData;
      const {
        customer_id: crnID,
        coc_sample_id: sdID,
        sample_type: stID,
        display_name: testName,
      } = sampleDetail;
      const poID = reportData["PO#"];
      const table = form.map((row) => ({
        ...row,
        Pesticide: row.compound,
        Result: row.compound_result,
      }));
      const rpDate = moment(baseForm.getFieldValue("report_date").$d).format("MM/DD/YYYY");
      const rdID = baseForm.getFieldValue("received_date") ? moment(baseForm.getFieldValue("received_date").$d).format("MM/DD/YYYY") : received_date;
      const invalidCountryCodeCols = ["instrument_id"];
      const countryCodes = tableColumns.slice(2, tableColumns.length - 1)
        .filter((item) => !invalidCountryCodeCols.includes(item.dataIndex))
        .map((item) => item.dataIndex);
      const { blobURL, pdf } = await GeneratePDF({
        countryCodes,
        table,
        toID,
        rpID,
        rpDate,
        crnID,
        rdID,
        poID,
        sdID,
        stID,
        testName,
      });

      showPreview(blobURL, pdf, { ...reportData, report_date: rpDate });
    } catch (e) {
      toast.error("Something went wrong with previewing the pdf");
    }
  };

  /**
   * Make an API call to fetch the report ID and customer information
   */
  const generateReport = async (tableFormValues) => {
    const {
      customer_id,
      workorder_id,
      sample_id,
      test_name,
    } = sampleDetail;

    const payload = {
      customer_id,
      workorder_id,
      sample_id: encodeURIComponent(sample_id),
      test_name,
    };
    const { success, result, message } = await generatePesticideReportHeader(payload);
    if (success) {
      await generatePdfPreview(result, tableFormValues);
    } else {
      toast.error(message);
    }
    setFormLoading(false);
  };

  const handleValidateFormError = (e) => {
    console.error("Validate Form Error", e);
    setFormLoading(false);
  };

  /**
   * If we found the original commodity name based on the current input value.
   * If no record found in the original commodity list, then we use the current input value
   */
  const getValidCommodityItem = (input) => {
    const item = commodityNameToItem[input.toLowerCase()];
    const commodity = item ? item.commodity : input;
    const code = item ? item.code : -1;

    return { commodity, code };
  };

  /**
   * For Fungicides test
   * 1. For delete, when deleting the last row data, we should set the last row data to the default value
   * 2. For add, when adding row data, if there is only one row data and the row data is the default value, then we need to reset the whole row to empty data
   * @param {*} prevData can be a form data or dataSource
   * @param {*} wordCheck either "" or "all tested"
   * @returns {boolean} true if we need to reuse the first row and reset the data
   */
  const shouldReuseFirstRow = (prevData, wordCheck = "") => {
    if (prevData.length !== 1) return false;
    return prevData.length === 1 && sampleDetail.defaultCompoundData && sampleDetail.defaultCompoundData.compound.toLowerCase().includes("fungicides") && dataSource[0].compound.toLowerCase().includes(wordCheck);
  };

  /**
   * Reset the MRL value for all countries based on the fieldValue
   */
  const resetMrlValue = (form, tableData, rowIndex, fieldValue) => {
    const excludeKeys = [
      "compound",
      "compound_result",
      "compound_row_action",
    ];
    const countries = tableColumns.map((item) => item.dataIndex).filter((key) => !excludeKeys.includes(key));
    if (form) {
      countries.forEach((key) => form[rowIndex].setFieldValue(key, fieldValue));
    } else if (tableData) {
      countries.forEach((key) => {
        tableData[rowIndex][key] = fieldValue;
      });
    }
  };

  /**
   * Delete the selected row from the tableForm and from the dataSource
   */
  const handleDeleteRow = async (index) => {
    const reuseFirstRow = shouldReuseFirstRow(tableForm);
    if (!reuseFirstRow) {
      // Update the map counter when deleting a row
      checkAndUpdateCompoundMap(null, tableForm[index].getFieldValue("compound"));
    } else { // delete the current row compound data in the hashmap
      compoundCounterMap.delete(tableForm[index].getFieldValue("compound").toLowerCase());
    }
    // Delete the row from the tableForm
    setTableForm((prevForms) => {
      const newForms = [...prevForms];
      if (!reuseFirstRow) newForms.splice(index, 1);
      if (newForms.length === 1 && sampleDetail.showDefaultCompound && reuseFirstRow) {
        newForms[0].setFieldValue("compound", sampleDetail.defaultCompoundData.compound);
        newForms[0].setFieldValue("compound_result", sampleDetail.defaultCompoundData.compound_result);
        resetMrlValue(newForms, null, index, "N/A");
      } else if (newForms.length === 1 && sampleDetail.showDefaultCompound) {
        newForms[0].setFieldValue("compound", sampleDetail.defaultCompoundData.compound);
        newForms[0].setFieldValue("compound_result", sampleDetail.defaultCompoundData.compound_result);
      }
      return newForms;
    });
    setDataSource((prevData) => {
      const newData = [];
      prevData.forEach((item, idx) => {
        if (idx !== index || reuseFirstRow) {
          newData.push({ ...item, rowIndex: newData.length });
        }
      });
      if (newData.length === 1 && reuseFirstRow) {
        newData[0].compound = sampleDetail.defaultCompoundData.compound;
        newData[0].compound_result = sampleDetail.defaultCompoundData.compound_result;
        newData[0].editable = false;
        resetMrlValue(null, newData, index, "N/A");
        setNewRowKey((prevKey) => {
          const newKey = prevKey + 1;
          newData[0].key = newKey;
          return newKey;
        });
      } else if (newData.length === 1 && sampleDetail.showDefaultCompound) {
        newData[0].compound = sampleDetail.defaultCompoundData.compound;
        newData[0].compound_result = sampleDetail.defaultCompoundData.compound_result;
      }
      return newData;
    });
  };

  const getNewTableForm = (prevForms, wordCheck) => {
    if (shouldReuseFirstRow(prevForms, wordCheck)) {
      return [...prevForms];
    }
    return [{}, ...prevForms];
  };

  /**
   * Add a new row to the tableForm and table dataSource
   */
  const handleAddRow = async () => {
    try {
      if (readyP && tableForm.length > 0) {
        await tableForm[0].validateFields(["compound"]);
      }
      setTableForm((prevForms) => {
        const newForms = getNewTableForm(prevForms, "all tested");
        const lastIdx = newForms.length - 1;
        // Change the last row's compound name accordingly
        // If there are more than one compound the last compound name is "Other Tested Pesticides Below LOQ*", otherwise it should be "All Tested Pesticides Below LOQ*"
        if (newForms.length > 1 && sampleDetail.defaultCompoundData && newForms[lastIdx].getFieldValue("compound") === sampleDetail.defaultCompoundData.compound && sampleDetail.defaultCompoundData.compound.toLowerCase().includes("pesticides")) {
          newForms[lastIdx].setFieldValue("compound", "Other Tested Pesticides Below LOQ*");
        }
        if (newForms.length === prevForms.length) {
          newForms[lastIdx].setFieldValue("compound", "");
          newForms[lastIdx].setFieldValue("compound_result", "");
          resetMrlValue(newForms, null, lastIdx, "");
        }
        return newForms;
      });

      setNewRowKey((prevKey) => {
        const newKey = prevKey + 1;
        // Update dateSource
        setDataSource((prevData) => {
          const newRow = { key: newKey, editable: true, rowIndex: 0 };
          const excludeKeys = ["key", "editable", "rowIndex"];
          keys(prevData[0]).forEach((itemKey) => {
            if (!excludeKeys.includes(itemKey)) {
              newRow[itemKey] = "";
            }
          });
          let newData = [newRow];
          prevData.forEach((item, idx) => {
            // Change the last row's compound name accordingly
            // If there are more than one compound the last compound name is "Other Tested Pesticides Below LOQ*", otherwise it should be "All Tested Pesticides Below LOQ*"
            const newCompound = idx === prevData.length - 1 && sampleDetail.showDefaultCompound && sampleDetail.test_name.toLowerCase().includes("pesticide") ? "Other Tested Pesticides Below LOQ*" : item.compound;
            newData.push({ ...item, rowIndex: newData.length, compound: newCompound });
          });
          if (shouldReuseFirstRow(prevData, "all tested")) {
            newData = [{
              ...prevData, key: newKey, editable: true, compound: "", compound_result: "", rowIndex: 0,
            }];
            resetMrlValue(null, newData, 0, "");
          }
          return newData;
        });
        return newKey;
      });
    } catch (e) {
      console.error("handleAddRow ~ e:", e);
    }
  };

  /**
   * Create the current compound list based on the form values
   */
  const getCurrentCompoundList = (form, currRowIdx) => {
    const compounds = [];
    const excludeCompounds = sampleDetail.defaultCompoundData ? [sampleDetail.defaultCompoundData.compound, "Other Tested Pesticides Below LOQ*"] : ["Other Tested Pesticides Below LOQ*"];
    form.forEach((row, idx) => {
      if (currRowIdx === -1 || idx === currRowIdx) { // currRowIdx = -1 means pass all compounds
        const { compound } = row;
        if (compound && !excludeCompounds.includes(compound)) {
          const { name, code } = getValidCompoundItem(compound);
          compounds.push({ name, code });
        }
      }
    });
    return compounds;
  };

  /**
   * Update the latest mrl values either in all rows and for a specific row
   */
  const updateMrlValuesForEachRow = (newMrlValues, form, targetRowIndex = -1) => {
    newMrlValues.forEach((compoundData) => {
      const { name, data } = compoundData;
      form.forEach((row, rowIdx) => {
        if (row.compound.toLowerCase() === name.toLowerCase()) {
          data.forEach((mrlData) => {
            const { country, mrl_value } = mrlData;
            const countryKey = country === "EEC" ? "EU" : country;
            // Update target row only
            if (targetRowIndex > -1) {
              if (rowIdx === targetRowIndex) {
                tableForm[rowIdx].setFieldValue(countryKey, mrl_value);
              }
            } else { // Update all row data
              tableForm[rowIdx].setFieldValue(countryKey, mrl_value);
            }
          });
        }
      });
    });
  };

  /**
   * Make an API call to fetch the latest mrl value based on the updated commodity value
   */
  const handleCommodityChange = async (name, code) => {
    try {
      if (!name) return;
      if (readyP) {
        setFormDisabled(true);
        const tableFormValues = await Promise.all(tableForm.map((form) => form.validateFields(["compound"])));
        await baseForm.validateFields();
        const {
          customer_id,
          customer_countries,
        } = sampleDetail;
        const payload = {
          customer_id,
          commodity: { name, code },
          compounds: getCurrentCompoundList(tableFormValues, -1),
          customer_countries,
        };
        const { success, result, message } = await getCommodityMrlValues(payload);
        if (success) {
          updateMrlValuesForEachRow(result.values, tableFormValues);
          setSampleDetail((prevState) => {
            const newState = { ...prevState, commodity: name };
            return newState;
          });
        } else {
          toast.error(message);
        }
        setFormDisabled(false);
      } else {
        await baseForm.validateFields(["commodity"]);
      }
    } catch (e) {
      handleValidateFormError(e);
    }
  };

  /**
   * Make an API call to fetch the latest mrl values for all current compounds based on the current commodity value
   */
  const handleCompoundChange = async (rowIndex) => {
    try {
      if (readyP) {
        const tableFormValues = await Promise.all(tableForm.map((form) => form.validateFields(["compound"])));
        await baseForm.validateFields();
        setFormDisabled(true);
        const {
          customer_id,
          customer_countries,
        } = sampleDetail;
        const commodityFormVal = baseForm.getFieldValue("commodity");
        const { commodity: name, code } = commodityNameToItem[commodityFormVal.toLowerCase()] ?? { commodity: commodityFormVal, code: -1 };
        const payload = {
          customer_id,
          commodity: { name, code },
          compounds: getCurrentCompoundList(tableFormValues, rowIndex),
          customer_countries,
        };
        const { success, result, message } = await getCommodityMrlValues(payload);
        if (success) {
          updateMrlValuesForEachRow(result.values, tableFormValues, rowIndex);
        } else {
          toast.error(message);
        }
        setFormDisabled(false);
      } else if (rowIndex !== -1) {
        tableForm[rowIndex].validateFields(["compound"]);
      }
    } catch (e) {
      handleValidateFormError(e);
    }
  };

  const handleClosePreview = (refreshList) => {
    setPdfModalData({
      open: false,
      pdfUrl: null,
      pdf: null,
    });
    // After the form is saved, we need to close the entire popup and back to the parent list view
    if (refreshList) {
      closeModal(true);
    }
  };

  const handleCommodityOnBlurEnter = () => {
    const formValue = baseForm.getFieldValue("commodity");
    if (formValue.toLowerCase() === sampleDetail.commodity.toLowerCase()) return; // If the current commodity does not change, then no need to make an API call
    const { commodity, code } = getValidCommodityItem(formValue);
    handleCommodityChange(commodity, code);
  };

  /**
   * This will call the pesticidedata/ to update the latest pesticide data in the database,
   * then it will do either generate PDF or close the modal
  */
  const handleSaveForm = async (action) => {
    try {
      setFormLoading(true);
      const tableFormValues = await Promise.all(tableForm.map((form) => form.validateFields()));
      const baseFormValues = await baseForm.validateFields();
      if (tableFormValues && baseFormValues) {
        const {
          customer_id,
          workorder_id,
          genista_id,
          sample_id,
          sample_status: status,
          test_name,
        } = sampleDetail;
        const { commodity } = getValidCommodityItem(baseFormValues.commodity);
        const values = getCompoundValuesList(tableFormValues);
        const payload = {
          user_id: getUser().id,
          list: [{
            commodity,
            commodity_changed: commodity !== parentSampleDetail.commodity,
            customer_id,
            workorder_id,
            genista_id,
            sample_id,
            status,
            values,
            instrument_ids: baseFormValues.instrument_ids,
          }],
          action,
          test_name,
        };

        const { success, message, result } = await uploadPesticideReportData(payload);
        if (success) {
          setLatestPesticideData(result[0].result); // Set the latest pesticide data so that we can use it in the pdf preview component
          setFetchBaseList(true);
          if (readyP) {
            generateReport(tableFormValues);
          } else {
            const toastMessage = action === "Save" ? "Pesticides data saved successfully" : "Pesticides data submitted successfully";
            toast.success(toastMessage);
            closeModal(true);
          }
        } else {
          toast.error(message);
          setFormLoading(false);
        }
      }
    } catch (e) {
      handleValidateFormError(e);
    }
  };

  const handleSubmitConfirm = async () => {
    if (readyP) {
      handleSaveForm("Submit");
    } else {
      try {
        await Promise.all(tableForm.map((form) => form.validateFields()));
        await baseForm.validateFields();
        setShowSubmitModal(true);
      } catch (e) {
        handleValidateFormError(e);
      }
    }
  };

  const reportReceivedDateValidator = () => {
    if (!baseForm.getFieldValue("received_date")) return Promise.resolve();
    const reportDate = moment(baseForm.getFieldValue("report_date").$d);
    const receivedDate = moment(baseForm.getFieldValue("received_date").$d);
    if (reportDate < receivedDate) {
      return Promise.reject(new Error("Report Date should not be older than the Received Date"));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      destroyOnClose
      centered
      open
      maskClosable={false}
      afterClose={() => setFormLoading(false)}
      footer={(
        <div className="ChemPesticidesTable__Footer">
          <Button onClick={() => closeModal(fetchBaseList)} type="text" disabled={formLoading || formDisabled}>
            Close
          </Button>
          {!readyP && (
            <Button onClick={() => handleSaveForm("Save")} type="default" disabled={formLoading || formDisabled || tableForm.length === 0}>
              Save
            </Button>
          )}
          {showSubmit && (
          <Button onClick={() => handleSubmitConfirm()} type="primary" disabled={formLoading || formDisabled || tableForm.length === 0}>
            {readyP ? "Save and Preview" : "Submit"}
          </Button>
          )}
        </div>
    )}
      title="Sample Detail"
      className="ChemPesticidesModal"
      rootClassName="ChemPesticidesModal__Root"
      width="90vw"
    >
      <Form form={baseForm} preserve={false}>
        <div className="ChemPesticidesModal__SampleInfo">
          {pesticideSampleInfo.map((item, idx) => {
            let inputNode = (
              <Form.Item
                className="ChemPesticidesTable__FormItem"
                name={item.key}
                initialValue={sampleDetail?.[item.key]}
              >
                <span>{sampleDetail?.[item.key]}</span>
              </Form.Item>
            );
            if (item.key === "commodity") {
              inputNode = (
                <Form.Item
                  preserve={false}
                  className="ChemPesticidesTable__FormItem"
                  name={item.key}
                  initialValue={sampleDetail?.[item.key]}
                  rules={[
                    {
                      required: true,
                      message: "Please enter a commodity",
                    },
                  ]}
                >
                  <AutoComplete
                    allowClear
                    className="ChemPesticidesModal__Commodity"
                    disabled={formLoading || formDisabled}
                    options={sampleDetail?.all_commodities.map((commodityItem) => ({ value: commodityItem.commodity, code: commodityItem.code }))}
                    filterOption={(inputValue, option) => option.value?.toLowerCase().startsWith(inputValue?.toLowerCase())}
                    onSelect={(value, option) => handleCommodityChange(value, option?.code)}
                    onBlur={() => handleCommodityOnBlurEnter()}
                  >
                    <Input onPressEnter={() => handleCommodityOnBlurEnter()} />
                  </AutoComplete>
                </Form.Item>
              );
            }
            if (item.key === "instrument_ids") {
              inputNode = (
                <Form.Item
                  preserve={false}
                  className="ChemPesticidesTable__FormItem"
                  name={item.key}
                  initialValue={sampleDetail?.instruments_list ?? []}
                  rules={[
                    {
                      required: true,
                      message: "Please select an instrument",
                    },
                  ]}
                >
                  <Checkbox.Group disabled={formLoading || formDisabled}>
                    {instrumentList.map((instrumentItem, instrumentIdx) => <Checkbox key={instrumentIdx} value={instrumentItem.id}>{instrumentItem.instrument_name}</Checkbox>)}
                  </Checkbox.Group>
                </Form.Item>
              );
            }
            if (item.key === "report_date" || item.key === "received_date") {
              inputNode = (
                <Form.Item
                  className="ChemPesticidesTable__FormItem--date"
                  preserve={false}
                  name={item.key}
                  initialValue={item.key === "received_date" ? "" : dayjs(sampleDetail?.[item.key])}
                  rules={[
                    { validator: reportReceivedDateValidator },
                  ]}
                >
                  <DatePicker
                    showNow
                    format="MM/DD/YYYY"
                    allowClear={item.key === "received_date"}
                    placeholder=""
                    disabled={formLoading || formDisabled}
                    onChange={() => baseForm.validateFields(item.key === "report_date" ? ["received_date"] : ["report_date"])}
                  />
                </Form.Item>
              );
            }
            return (
              <span key={idx} className="ChemPesticidesModal__SampleInfo__Span">
                <label className="ChemPesticidesModal__SampleInfo__Label">
                  {item.label}
                  :
                </label>
                {inputNode}
              </span>
            );
          })}
        </div>
      </Form>
      <div className="ChemPesticidesModal__AddItem">
        <Button type="primary" onClick={handleAddRow} disabled={formLoading || formDisabled}>
          Add Compound
        </Button>
      </div>
      <Table
        sticky
        bordered
        className="ChemPesticidesTable ChemPesticidesTable--fixedHeight"
        tableLayout="fixed"
        scroll={sampleDetail?.sample_status === "Ready-P" ? {
          x: 1300,
          y: 500,
        } : { y: 500 }}
        dataSource={dataSource}
        pagination={false}
        loading={formLoading || formDisabled}
        components={{
          body: {
            row: ChemPesticidesTableRow,
            cell: ChemPesticidesTableCell,
          },
        }}
        onRow={(record, index) => (
          {
            record, index, saveFormInstance,
          }
        )}
        columns={tableColumns.map((col) => ({
          ...col,
          onCell: (record) => ({
            sampleDetail,
            record,
            dataIndex: col.dataIndex,
            title: col.title,
            loading: formLoading || formDisabled,
            handleDeleteRow,
            handleCompoundChange,
            compoundCounterMap,
            checkAndUpdateCompoundMap,
          }),
        }))}
      />
      <ChemPesticidePreview
        pdfModalData={pdfModalData}
        latestPesticideData={latestPesticideData}
        sampleDetail={sampleDetail}
        handleClosePreview={(refreshList) => handleClosePreview(refreshList)}
      />
      {showSubmitModal && (
      <ConfirmationModal
        title="Submit Confirmation"
        open={showSubmitModal}
        bodyText="Are you sure you want to submit the pesticide data?"
        okText="Yes"
        cancelText="No"
        loading={formLoading}
        onCancel={() => setShowSubmitModal(false)}
        onOk={() => handleSaveForm("Submit")}
        width="auto"
      />
      )}
    </Modal>
  );
}

export default ChemPesticidesModal;
